import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  BookOpen,
  Brain,
  Upload,
  BarChart2,
  Settings,
  KeyRound,
  LogOut,
  Menu,
  X,
  ShieldCheck,
  Plus,
  Cpu,
} from 'lucide-react';
import { Modal } from './Modal';
import { fetchRegisterOptions, verifyRegistration } from '../api/auth';
import { startRegistration } from '@simplewebauthn/browser';
import { useToast } from '../context/ToastContext';

export type NavTab = 'study' | 'wordbook' | 'import' | 'stats' | 'settings' | 'integrations';

interface NavbarProps {
  currentTab: NavTab;
  onSelectTab: (tab: NavTab) => void;
}

export const Navbar: React.FC<NavbarProps> = ({ currentTab, onSelectTab }) => {
  const { user, authenticated, logout } = useAuth();
  const { showToast } = useToast();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [passkeyModalOpen, setPasskeyModalOpen] = useState(false);
  const [passkeyName, setPasskeyName] = useState('');
  const [registering, setRegistering] = useState(false);

  const navItems = [
    { id: 'study' as NavTab, label: '今日复习', icon: Brain },
    { id: 'wordbook' as NavTab, label: '词库管理', icon: BookOpen },
    { id: 'import' as NavTab, label: '生词导入', icon: Upload },
    { id: 'stats' as NavTab, label: '学习统计', icon: BarChart2 },
    { id: 'settings' as NavTab, label: '算法设置', icon: Settings },
    { id: 'integrations' as NavTab, label: 'MCP/API', icon: Cpu },
  ];

  const handleAddPasskey = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegistering(true);
    try {
      // 1. Get options from server
      const optionsRes = await fetchRegisterOptions(passkeyName.trim() || undefined);
      // 2. Browser WebAuthn registration
      const credential = await startRegistration(optionsRes.publicKey);
      // 3. Verify with server
      await verifyRegistration(optionsRes.challenge_id, credential);
      showToast('Passkey 绑定成功！你可以使用此设备快捷免密登录。', 'success');
      setPasskeyModalOpen(false);
      setPasskeyName('');
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        showToast('用户取消了 Passkey 验证或超时', 'warning');
      } else {
        showToast(err.message || 'Passkey 绑定失败', 'error');
      }
    } finally {
      setRegistering(false);
    }
  };

  return (
    <>
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-8">
              <div
                onClick={() => onSelectTab('study')}
                className="flex items-center gap-2.5 cursor-pointer group"
              >
                <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-500 flex items-center justify-center text-white shadow-sm shadow-emerald-200 group-hover:scale-105 transition-transform">
                  <Brain className="w-5 h-5" />
                </div>
                <div>
                  <span className="font-bold text-xl tracking-tight text-slate-900 group-hover:text-emerald-700 transition-colors">
                    Lexi
                  </span>
                  <span className="hidden sm:inline-block ml-2 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium border border-emerald-200">
                    FSRS 智能记忆
                  </span>
                </div>
              </div>

              {/* Desktop Nav Tabs */}
              {authenticated && (
                <nav className="hidden md:flex items-center gap-1">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = currentTab === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => onSelectTab(item.id)}
                        className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${
                          isActive
                            ? 'bg-emerald-50 text-emerald-800 shadow-sm'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/80'
                        }`}
                      >
                        <Icon className={`w-4 h-4 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                        {item.label}
                      </button>
                    );
                  })}
                </nav>
              )}
            </div>

            {/* Right Action buttons */}
            <div className="flex items-center gap-3">
              {authenticated ? (
                <>
                  <div className="hidden lg:flex items-center gap-2 text-xs bg-slate-100 py-1.5 px-3 rounded-full text-slate-700">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                    <span>{user?.name || user?.id || '学习者'}</span>
                  </div>

                  <button
                    onClick={() => setPasskeyModalOpen(true)}
                    title="添加此设备 Passkey"
                    className="flex items-center gap-1.5 text-xs font-medium text-slate-700 hover:text-emerald-700 bg-white hover:bg-emerald-50 border border-slate-200 hover:border-emerald-200 py-1.5 px-2.5 rounded-lg transition-colors"
                  >
                    <KeyRound className="w-3.5 h-3.5 text-slate-500" />
                    <span className="hidden sm:inline">Passkey 管理</span>
                  </button>

                  <button
                    onClick={logout}
                    title="退出登录"
                    className="text-slate-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>

                  {/* Mobile menu toggle */}
                  <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="md:hidden text-slate-600 hover:text-slate-900 p-2 rounded-lg hover:bg-slate-100"
                  >
                    {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>

        {/* Mobile menu dropdown */}
        {authenticated && mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white px-4 pt-2 pb-4 space-y-1 shadow-lg">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    onSelectTab(item.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-800'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {item.label}
                </button>
              );
            })}
          </div>
        )}
      </header>

      {/* Add Passkey Modal */}
      <Modal
        isOpen={passkeyModalOpen}
        onClose={() => setPasskeyModalOpen(false)}
        title="添加 Passkey 凭据"
      >
        <form onSubmit={handleAddPasskey} className="space-y-4">
          <p className="text-sm text-slate-600 leading-relaxed">
            将当前设备（如 MacBook Touch ID、iPhone Face ID 或 Windows Hello）注册为登录凭据，免密安全登录。
          </p>

          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
              凭据名称或设备备注（可选）
            </label>
            <input
              type="text"
              value={passkeyName}
              onChange={(e) => setPasskeyName(e.target.value)}
              placeholder="例如：MacBook Pro Touch ID"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-3">
            <button
              type="button"
              onClick={() => setPasskeyModalOpen(false)}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={registering}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              {registering ? '正在唤起设备生物识别...' : '立即注册当前设备'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
};
